/**
 * Email Verification Banner Component
 *
 * Displays a dismissible banner prompting unverified users to verify their email.
 * Shows at the top of protected pages for users with email_verified === false.
 */

import React, { useState } from 'react';
import Alert from './Alert';
import api from '@/services/api';

interface EmailVerificationBannerProps {
  userEmail: string;
  onDismiss?: () => void;
}

export const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({
  userEmail,
  onDismiss,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendError(null);

    try {
      await api.post('/auth/resend-verification', {
        email: userEmail,
      });

      setResendSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (error: any) {
      setResendError(
        error.response?.data?.message ||
        'Failed to resend verification email. Please try again.'
      );
    } finally {
      setIsResending(false);
    }
  };

  if (resendSuccess) {
    return (
      <Alert
        type="success"
        title="Verification Email Sent"
        message="Check your inbox for the verification link."
        dismissible={true}
        onDismiss={onDismiss}
        className="mb-4"
      />
    );
  }

  return (
    <Alert
      type="warning"
      title="Email Verification Required"
      message={
        resendError ||
        `Please verify your email address (${userEmail}) to access all features. Check your inbox for the verification link.`
      }
      dismissible={true}
      onDismiss={onDismiss}
      className="mb-4"
      actions={[
        {
          label: isResending ? 'Sending...' : 'Resend Verification Email',
          onClick: handleResendEmail,
          variant: 'primary',
        },
      ]}
    />
  );
};

export default EmailVerificationBanner;
